import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "./SevenInchesPage.css";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useCompare } from "../../context/CompareContext";
import FrequentlyBoughtTogether from "./FrequentlyBoughtTogether";
import SimilarProducts from "./SimilarProducts";
import RelatedProducts from "./RelatedProducts";
import { CartIcon, DownloadIcon } from "../../components/ActionIcons";
import { IMAGE_COMING_SOON } from "../../imageComingSoon";
const svgIcons = {
  display: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="12" y1="16" x2="12" y2="20" />
    </svg>
  ),
  resolution: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M7 8h2v4H7zM12 8h2v4h-2z" />
    </svg>
  ),
  touch: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2" />
      <path d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2" />
      <path d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8" />
      <path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-2a8 8 0 0 1-8-8 2 2 0 1 1 4 0" />
    </svg>
  ),
  brightness: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
    </svg>
  ),
  hdmi: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="2" y="7" width="20" height="10" rx="2" />
      <path d="M7 7V5h10v2" />
    </svg>
  ),
  panel: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <rect x="7" y="7" width="10" height="10" rx="1" />
    </svg>
  ),
  viewing: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  led: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </svg>
  ),
};

const PLACEHOLDER_THUMB = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><rect width='80' height='80' rx='8' fill='%23e8f5f0'/><rect x='20' y='24' width='40' height='28' rx='4' fill='none' stroke='%231a7f63' stroke-width='2'/></svg>`;
const PLACEHOLDER_MAIN = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='450' viewBox='0 0 600 450'><rect width='600' height='450' fill='%23f0f9f4'/><text x='300' y='230' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%231a7f63' opacity='0.6'>Image not found</text></svg>`;

const Icon = ({ name }) => (
  <span className="hmi-svg-icon">{svgIcons[name] || svgIcons.display}</span>
);

const Subtitle = ({ template, highlight }) => {
  const parts = template.split("{highlight}");
  return (
    <p className="hmi-subtitle">
      {parts[0]}<strong>{highlight}</strong>{parts[1]}
    </p>
  );
};

const PdfModal = ({ url, onClose }) => (
  <div className="pdf-modal-overlay" onClick={onClose}>
    <div className="pdf-modal-box" onClick={e => e.stopPropagation()}>
      <div className="pdf-modal-header">
        <span className="pdf-modal-title">📄 Datasheet Preview</span>
        <button className="pdf-modal-close" onClick={onClose}>✕</button>
      </div>
      <iframe src={url} title="Datasheet Preview" className="pdf-modal-iframe" />
    </div>
  </div>
);

// Always shown inline (no trigger button, no dismiss) right below the CTA
// row, themed to match the rest of the page instead of popping up as a
// separate dialog.
const BulkPricingInline = ({ priceTiers, activeTier }) => (
  <div className="bulk-pricing-inline">
    <div className="bulk-pricing-inline-header">
      <span className="bulk-pricing-inline-title"> Bulk Pricing</span>
    </div>
    <PricingSlabTable priceTiers={priceTiers} activeTier={activeTier} />
  </div>
);

// Which slab tier a given order quantity currently falls into -- shared
// between the price display, Add to Cart, and the slab table's row
// highlight so all three always agree on the same tier. Returns:
//   { price, tier, overLimit, limitQty }
// price is null (with overLimit true) once qty exceeds the highest tier's
// maxQty and that tier isn't open-ended -- there's no valid unit price to
// show or charge past that point, only a "contact sales" prompt.
function resolveSlabPrice(qty, basePrice, priceTiers) {
  if (!Array.isArray(priceTiers) || priceTiers.length === 0) {
    return { price: basePrice, tier: null, overLimit: false, limitQty: null };
  }
  const sorted = [...priceTiers].sort((a, b) => Number(a.qty) - Number(b.qty));
  const topTier = sorted[sorted.length - 1];
  if (topTier.maxQty != null && qty > Number(topTier.maxQty)) {
    return { price: null, tier: null, overLimit: true, limitQty: Number(topTier.maxQty) };
  }
  const matched = sorted.find((tier) => {
    const min = Number(tier.qty);
    const max = tier.maxQty == null ? Infinity : Number(tier.maxQty);
    return qty >= min && qty <= max;
  });
  if (matched) {
    return { price: Number(matched.price), tier: matched, overLimit: false, limitQty: null };
  }
  // Below every tier's starting quantity (e.g. MOQ hasn't been reached yet
  // in the input) -- List Price/MOQ are the primary/default price.
  return { price: basePrice, tier: null, overLimit: false, limitQty: null };
}

// ✅ ENHANCED PRICING SLAB TABLE COMPONENT
const PricingSlabTable = ({ priceTiers, activeTier }) => {
  if (!priceTiers || !Array.isArray(priceTiers) || priceTiers.length === 0) {
    return null;
  }

  const lowestPriceTier = priceTiers.reduce((prev, current) =>
    (parseFloat(current.price) < parseFloat(prev.price)) ? current : prev
  );

  return (
    <div className="pricing-slab-wrapper">

      <div className="pricing-slab-table">
        <div className="pricing-slab-header">
          <div className="slab-header-qty">Quantity Range</div>
          <div className="slab-header-price">Price per Unit</div>
        </div>

        {priceTiers.map((tier, index) => (
          <div
            key={index}
            className="pricing-slab-row"
            data-best={lowestPriceTier.qty === tier.qty ? 'true' : 'false'}
            data-active={activeTier && activeTier.qty === tier.qty ? 'true' : 'false'}
          >
            <div className="slab-qty">
              {tier.maxQty != null ? `${tier.qty} - ${tier.maxQty}` : `${tier.qty}+`}
            </div>
            <div className="slab-price">
              ₹{parseFloat(tier.price).toFixed(2)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── tixaru datasheet sections (Optical/Electrical/Reliability tables,
   Mechanical Specs/Drawings images, Product Videos) -- each renders
   nothing at all when its data is empty, same "hide the whole section"
   rule as PricingSlabTable above. Inline-styled (not the site's existing
   CSS classes) since these are real multi-column tables, not the
   label/value pairs .specs-row is built for. ─────────────────────── */
const SpecDataTable = ({ title, headers, rows }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="specs-group">
      <div className="spec-group-heading">{title}</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr>
              {headers.map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "8px 10px",
                    borderBottom: "2px solid #e0e0e0",
                    whiteSpace: "nowrap",
                    color: "#555",
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} style={{ padding: "8px 10px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                    {cell || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SpecImageBlock = ({ title, src }) => {
  if (!src) return null;
  return (
    <div className="specs-group">
      <div className="spec-group-heading">{title}</div>
      <img
        src={src}
        alt={title}
        style={{ maxWidth: "100%", height: "auto", border: "1px solid #eee", borderRadius: 6 }}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    </div>
  );
};

// Matches any common YouTube URL shape (watch, youtu.be, embed, shorts) --
// same pattern used server-side (biba-pip's extract_youtube_id) -- just so
// a thumbnail can load straight from the browser without a round trip.
function youtubeThumbnail(url) {
  const match = String(url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
}

// Compliance/marketing badges (BIS Certified, Make in India, ...) from the
// item's Product Tags section on tixaru -- shows the uploaded logo, or the
// tag's name as a small pill when no logo's been uploaded for it yet.
const TagBadges = ({ tags }) => {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="hmi-tag-badges">
      {tags.map((t, i) =>
        t.logoUrl ? (
          <img
            key={i}
            className="hmi-tag-badge-logo"
            src={t.logoUrl}
            alt={t.name}
            title={t.name}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span key={i} className="hmi-tag-badge-text">{t.name}</span>
        )
      )}
    </div>
  );
};

const SpecVideosBlock = ({ videos }) => {
  if (!videos || videos.length === 0) return null;
  return (
    <div className="specs-group">
      <div className="spec-group-heading">Product Videos</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
        {videos.map((v, i) => {
          const thumb = youtubeThumbnail(v.url);
          return (
            <a
              key={i}
              href={v.url}
              target="_blank"
              rel="noreferrer"
              style={{ display: "block", textDecoration: "none", color: "inherit" }}
            >
              {thumb && (
                <img
                  src={thumb}
                  alt={v.title || "Video thumbnail"}
                  style={{ width: "100%", borderRadius: 6, border: "1px solid #eee" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              <div style={{ fontSize: 13, marginTop: 4, fontWeight: 500 }}>{v.title || "Watch Video"}</div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Zoom magnifier hook ─────────────────────────────────────── */
const useImageZoom = ({ wrapperRef, imgRef, lensRef, resultRef, resultImgRef, enabled }) => {
  const isHoveringRef = useRef(false);

  const computeRatios = useCallback(() => {
    const img = imgRef.current;
    const result = resultRef.current;
    const lens = lensRef.current;
    if (!img || !result || !lens) return { cx: 1, cy: 1 };
    return {
      cx: result.offsetWidth / lens.offsetWidth,
      cy: result.offsetHeight / lens.offsetHeight,
    };
  }, [imgRef, resultRef, lensRef]);

  const positionLens = useCallback((clientX, clientY) => {
    const wrapper = wrapperRef.current;
    const lens = lensRef.current;
    const img = imgRef.current;
    const result = resultRef.current;
    const resultImg = resultImgRef.current;
    if (!wrapper || !lens || !img || !result || !resultImg) return;

    const rect = wrapper.getBoundingClientRect();

    let x = clientX - rect.left - lens.offsetWidth / 2;
    let y = clientY - rect.top - lens.offsetHeight / 2;
    x = Math.max(0, Math.min(x, wrapper.offsetWidth - lens.offsetWidth));
    y = Math.max(0, Math.min(y, wrapper.offsetHeight - lens.offsetHeight));

    lens.style.left = x + "px";
    lens.style.top = y + "px";

    const { cx, cy } = computeRatios();
    resultImg.style.width = img.offsetWidth * cx + "px";
    resultImg.style.height = img.offsetHeight * cy + "px";
    resultImg.style.left = -(x * cx) + "px";
    resultImg.style.top = -(y * cy) + "px";
  }, [wrapperRef, lensRef, imgRef, resultRef, resultImgRef, computeRatios]);

  useEffect(() => {
    if (!enabled) return;

    const wrapper = wrapperRef.current;
    const lens = lensRef.current;
    const result = resultRef.current;
    const resultImg = resultImgRef.current;
    const img = imgRef.current;
    if (!wrapper || !lens || !result || !resultImg || !img) return;

    lens.style.display = "none";
    result.style.display = "none";

    const show = () => {
      if (window.innerWidth <= 1100) return;
      isHoveringRef.current = true;
      lens.style.display = "block";
      result.style.display = "block";
    };

    const hide = () => {
      isHoveringRef.current = false;
      lens.style.display = "none";
      result.style.display = "none";
    };

    const onMove = (e) => {
      if (!isHoveringRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      positionLens(clientX, clientY);
    };

    const onResize = () => {
      if (window.innerWidth <= 1100) hide();
    };

    wrapper.addEventListener("mouseenter", show, { passive: true });
    wrapper.addEventListener("mouseleave", hide, { passive: true });
    wrapper.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      wrapper.removeEventListener("mouseenter", show);
      wrapper.removeEventListener("mouseleave", hide);
      wrapper.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
    };
  }, [enabled, wrapperRef, imgRef, lensRef, resultRef, resultImgRef, positionLens]);
};

const SevenInchesPage = ({
  pageData,
  showQuantitySelector = false,
  showAddToCart = false,
  showPrice = false,
}) => {
  const { addToCart } = useCart();
  const { isInCompare, toggleCompare } = useCompare();
  const location = useLocation();
  const [activeImg, setActiveImg] = useState(0);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
const [quantity, setQuantity] = useState(1);

const [cartLimitMsg, setCartLimitMsg] = useState("");
  const [justAdded, setJustAdded] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  // Which spec section tab is open -- General Specs/Optical/Electrical/
  // Reliability/Mechanical/Videos, mirroring tixaru's own datasheet section
  // tabs. null until the available-tabs list is computed below, since which
  // tabs even exist depends on which sections this particular product has
  // data for.
  const [activeSpecTab, setActiveSpecTab] = useState(null);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const lensRef = useRef(null);
  const resultRef = useRef(null);
  const resultImgRef = useRef(null);
  const justAddedTimerRef = useRef(null);
  const ctaRowRef = useRef(null);

  const {
    product,
    images,
    bannersAboveSpecs,
    bannersBelowSpecs,
        productDetails,

    specs,
    applications,
    software,
    breadcrumb,
    pricing: pricingFromData,
    priceTiers,
    frequentlyBoughtTogether,
    similarProducts,
    relatedProducts,
    opticalCharacteristics,
    electricalAbsoluteMax,
    electricalDCCharacteristics,
    reliabilitySpecs,
    mechanicalSpecsImageUrl,
    mechanicalDrawingImageUrl,
    blockDiagramImageUrl,
    videoLinks,
    tags,
    generalSpecsNotes,

  } = pageData;
  // "Coming Soon" gets its own small corner tag on the product photo
  // instead of sitting in the general compliance/marketing badge strip
  // (BIS Certified, Make in India, ...) further down the page.
  const comingSoonTag = tags?.find((t) => (t.name || "").trim().toLowerCase() === "coming soon");
  const otherTags = tags?.filter((t) => (t.name || "").trim().toLowerCase() !== "coming soon");
useEffect(() => {
  setQuantity(Number(product?.moq) || 1);
}, [product?.moq]);
  console.log("Similar Products:", similarProducts);


  const hasPriceTiers = priceTiers && priceTiers.length > 0;
  const hasFbt = frequentlyBoughtTogether && frequentlyBoughtTogether.length > 0;
  const hasRelated = relatedProducts && relatedProducts.length > 0;

  // Spec sections as clickable tabs (General Specs/Optical/Electrical/
  // Reliability/Mechanical/Videos) instead of one long stacked page -- same
  // "only show a tab when the product actually has data for it" rule
  // tixaru's own datasheet section tabs use. Grouped once here so both the
  // tab bar and the active panel below read off the same list.
  const generalSpecsGroups =
    specs.length > 0
      ? Object.entries(
          specs.reduce((groups, s) => {
            const cat = s.category || "General";
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(s);
            return groups;
          }, {})
        )
      : [];

  const specTabs = [
    {
      key: "general",
      label: "General Specs",
      show: generalSpecsGroups.length > 0,
      content: (
        <div className="specs-tab-content">
          <div className="specs-table-scroll">
            <div className="specs-table">
              {generalSpecsGroups.map(([category, items]) => (
                <div className="specs-group" key={category}>
                  <div className="spec-group-heading">{category}</div>
                  <div className="specs-group-rows">
                    {items.map((s, i) => {
                      const lines = String(s.value).split("\n");
                      return (
                        <div className="specs-row" key={i}>
                          <div className="specs-row-label">{s.label}</div>
                          <div className="specs-row-value">
                            {lines.map((line, li) => (
                              <React.Fragment key={li}>
                                {line}
                                {li < lines.length - 1 && <br />}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "blockDiagram",
      label: "Block Diagram",
      show: Boolean(blockDiagramImageUrl),
      content: <SpecImageBlock title="Block Diagram" src={blockDiagramImageUrl} />,
    },
    {
      key: "optical",
      label: "Optical Characteristics",
      show: (opticalCharacteristics || []).length > 0,
      content: (
        <SpecDataTable
          title="Optical Characteristics"
          headers={["Parameter", "Symbol", "Condition", "Min", "Typ", "Max", "Unit", "Remarks"]}
          rows={(opticalCharacteristics || []).map((r) => [
            r.parameter, r.symbol, r.condition, r.min, r.typ, r.max, r.unit, r.remarks,
          ])}
        />
      ),
    },
    {
      key: "electrical",
      label: "Electrical Characteristics",
      show: (electricalAbsoluteMax || []).length > 0 || (electricalDCCharacteristics || []).length > 0,
      content: (
        <>
          <SpecDataTable
            title="Absolute Maximum Rating"
            headers={["Item", "Symbol", "Min", "Max", "Unit"]}
            rows={(electricalAbsoluteMax || []).map((r) => [r.item, r.symbol, r.min, r.max, r.unit])}
          />
          <SpecDataTable
            title="DC Characteristics"
            headers={["Item", "Symbol", "Min", "Typ", "Max", "Unit", "Remark"]}
            rows={(electricalDCCharacteristics || []).map((r) => [
              r.item, r.symbol, r.min, r.typ, r.max, r.unit, r.remark,
            ])}
          />
        </>
      ),
    },
    {
      key: "reliability",
      label: "Reliability Specs",
      show: (reliabilitySpecs || []).length > 0,
      content: (
        <SpecDataTable
          title="Reliability Specs"
          headers={["Test Item", "Test Conditions", "Result"]}
          rows={(reliabilitySpecs || []).map((r) => [r.testItem, r.testConditions, r.result])}
        />
      ),
    },
    {
      key: "mechanical",
      label: "Mechanical",
      show: Boolean(mechanicalSpecsImageUrl) || Boolean(mechanicalDrawingImageUrl),
      content: (
        <>
          <SpecImageBlock title="Mechanical Specs" src={mechanicalSpecsImageUrl} />
          <SpecImageBlock title="Mechanical Drawings" src={mechanicalDrawingImageUrl} />
        </>
      ),
    },
    {
      key: "videos",
      label: "Product Videos",
      show: (videoLinks || []).length > 0,
      content: <SpecVideosBlock videos={videoLinks} />,
    },
  ].filter((t) => t.show);

  const activeSpecTabKey = specTabs.some((t) => t.key === activeSpecTab) ? activeSpecTab : specTabs[0]?.key;
  const activeSpecTabContent = specTabs.find((t) => t.key === activeSpecTabKey)?.content ?? null;

  // MOQ is the floor when there's only one price -- but a slab table can
  // define real pricing below MOQ (e.g. small quantities at a higher per-
  // unit price), and a customer should be able to actually order that
  // quantity rather than being forced up to MOQ first. The floor relaxes
  // down to the lowest slab's starting quantity whenever one exists below
  // MOQ; it never goes below 1.
  const lowestTierQty = hasPriceTiers
    ? Math.min(...priceTiers.map((t) => Number(t.qty) || Infinity))
    : null;
  const effectiveMinQty = lowestTierQty != null
    ? Math.min(Number(product.moq) || 1, lowestTierQty)
    : Number(product.moq) || 1;

  // Only add the PDF thumbnail when there's a real datasheet behind it --
  // see TFTDetailPage.jsx, which no longer fabricates a fallback path for
  // products with nothing uploaded to "BIBA 2D PDF File".
  const allThumbs = product.datasheetUrl ? [...images, { src: "PDF", alt: "Datasheet PDF" }] : images;
  // No Front/Back Image on the item's Tixaru record -- "Image Coming Soon"
  // instead of a stock/blank photo (see TFTDetailPage.jsx, images is []
  // rather than a fallback path in that case).
  const currentImg = images[activeImg] || images[0] || { src: IMAGE_COMING_SOON, alt: "Image coming soon" };
  // Positional (last thumb) only means "PDF" when a PDF thumb actually got
  // added above -- without this guard, clicking the last real photo on a
  // product with no datasheet would wrongly show the PDF preview panel.
  const isPdfActive = Boolean(product.datasheetUrl) && activeImg === allThumbs.length - 1;

  const stockCount = Number(product.stock) || 0;

  // List Price/MOQ (product.price) stays the primary/default price. Once
  // slabs exist, the entered order quantity picks a different unit price
  // -- or, past the top slab's cutoff, no price at all (contact sales).
  const numericQty = Number(quantity) || 0;
  const basePrice = product.price != null && !isNaN(Number(product.price)) ? Number(product.price) : null;
  const slabResult = useMemo(
    () => resolveSlabPrice(numericQty, basePrice, priceTiers),
    [numericQty, basePrice, priceTiers]
  );
  const effectivePrice = slabResult.price;
  const overSlabLimit = slabResult.overLimit;
  const overSlabLimitQty = slabResult.limitQty;
  // Not yet priced -- show "-" instead of a number and keep Add to Cart
  // disabled rather than charging a fabricated/zero price (see
  // TFTDetailPage.jsx, which no longer defaults a blank price to 500).
  const hasPrice = effectivePrice != null && !isNaN(Number(effectivePrice));

  const partNo = product.badge.replace("Part No : ", "");

  // Keyed by the current URL rather than Part No -- some rows have no Part
  // No filled in on Tixaru, and this also means the same product added from
  // a listing card and from its own detail page always dedupes to one entry.
  const compareProduct = {
    id: location.pathname,
    partNo,
    title: product.title,
    image: images?.[0]?.src || null,
    price: basePrice,
    route: location.pathname,
    specs: specs.map((s) => ({ label: s.label, value: s.value })),
  };
  const inCompare = isInCompare(compareProduct.id);

  const increaseQty = () => {
    setQuantity((prev) => (Number(prev) || 0) + 1);
  };

  const decreaseQty = () => {
  const minQty = effectiveMinQty;

  setCartLimitMsg("");

  setQuantity((prev) => {
    const current = Number(prev) || minQty;
    return current > minQty ? current - 1 : minQty;
  });
};

  // Lets the person freely type any quantity, digit by digit -- even one
  // that's momentarily below the MOQ mid-edit (e.g. typing "150" passes
  // through "1" then "15" first). The MOQ floor is only enforced once they
  // finish, in the onBlur handlers below; clamping here on every keystroke
  // fought the browser's cursor position and made multi-digit entry below
  // the previous value impossible.
  const handleQtyChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setQuantity("");
      setCartLimitMsg("");
      return;
    }

    const num = Number(value);
    if (isNaN(num) || num < 0) return;

    setQuantity(num);
    setCartLimitMsg("");
  };

  // Shared by both the main Add to Cart button and the sticky mini bar
  // that appears once the main one scrolls out of view — same validation,
  // same success micro-interaction (brief checkmark on the button).
  const handleAddToCart = () => {
    const qty = Number(quantity) || 0;
    if (qty < 1) return;
    if (!hasPrice || overSlabLimit) return; // guards direct calls too, not just the disabled button
    setCartLimitMsg("");
   addToCart({
  partNo,
  title: product.title,
  image: images?.[0]?.src,
  quantity: qty,
  moq: Number(product.moq) || 1,
  price: Number(effectivePrice),
});
    setJustAdded(true);
    window.clearTimeout(justAddedTimerRef.current);
    justAddedTimerRef.current = window.setTimeout(() => setJustAdded(false), 1600);
  };

  useImageZoom({
    wrapperRef,
    imgRef,
    lensRef,
    resultRef,
    resultImgRef,
    enabled: !isPdfActive,
  });

  // Sticky mini Add-to-Cart bar: shows once the main CTA row has scrolled
  // out of view above the viewport, so the buy action stays reachable
  // while reading specs/reviews further down — standard on premium PDPs.
  useEffect(() => {
    const el = ctaRowRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBar(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => window.clearTimeout(justAddedTimerRef.current);
  }, []);

  // Scroll-reveal for below-the-fold cards (spec groups, resource/
  // application cards) — each fades + slides up the first time it enters
  // the viewport, staggered slightly (see the inline transitionDelay on
  // each card) instead of the whole grid just appearing at once. Re-runs
  // when the spec/resource/application lists change since those cards are
  // conditionally rendered and mount fresh each time.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const els = document.querySelectorAll(".reveal-on-scroll:not(.reveal-on-scroll--visible)");
    if (!els.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-on-scroll--visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [specs, applications]);

  return (
    <div className="hmi-page-layout">
      <div className="hmi-page-inner">

        {breadcrumb && (
          <div className="breadcrumb">
            {breadcrumb.map((item, index) => (
              <React.Fragment key={index}>
                {item.path ? (
                  <Link to={item.path}>
                    {item.label}
                  </Link>
                ) : (
                  <span className="breadcrumb-current">
                    {item.label}
                  </span>
                )}

                {index < breadcrumb.length - 1 && (
                  <span className="breadcrumb-separator">
                    /
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ══ TOP SECTION ══════════════════════════════════════════════ */}
        <div className="hmi-top-section">

          {/* ── LEFT col: gallery + software card ── */}
          <div className="hmi-image-col">
            <div className="hmis-gallery">
              <div className="gallery-thumbs">
                {allThumbs.map((thumb, i) => {
                  const isPdf = thumb.src === "PDF";
                  return (
                    <button
                      key={i}
                      className={`thumb-btn ${activeImg === i ? "active" : ""} ${isPdf ? "thumb-btn--pdf" : ""}`}
                      onClick={() => {
                        setActiveImg(i);
                        if (isPdf) { setPdfUrl(product.datasheetUrl); setShowPdf(true); }
                      }}
                      title={isPdf ? "View Datasheet PDF" : thumb.alt}
                    >
                      {isPdf ? (
                        <div className="thumb-pdf-inner">
                          <span className="thumb-pdf-icon">📄</span>
                          <span className="thumb-pdf-label">PDF</span>
                        </div>
                      ) : (
                        <img
                          src={thumb.src}
                          alt={thumb.alt}
                          onError={e => { e.currentTarget.src = PLACEHOLDER_THUMB; e.currentTarget.onerror = null; }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="gallery-main">
                {comingSoonTag && !isPdfActive && (
                  <span className="hmi-coming-soon-badge">Coming Soon</span>
                )}
                {isPdfActive ? (
                  <div className="pdf-preview-panel" onClick={() => setShowPdf(true)}>
                    <div className="pdf-preview-icon">📄</div>
                    <div className="pdf-preview-name">
                      {product.datasheetUrl?.split("/").pop() || "Datasheet.pdf"}
                    </div>
                    <div className="pdf-preview-hint">Click to preview</div>
                    <button className="pdf-preview-btn">Open PDF Preview</button>
                  </div>
                ) : (
                  <>
                    <div className="zoom-imgs-wrapper" ref={wrapperRef}>
                      <img
                        ref={imgRef}
                        key={activeImg}
                        src={currentImg.src}
                        alt={currentImg.alt}
                        onError={e => { e.currentTarget.src = PLACEHOLDER_MAIN; e.currentTarget.onerror = null; }}
                      />
                      <div className="zoom-lens" ref={lensRef} />
                    </div>

                    <div className="zoom-result" ref={resultRef}>
                      <img
                        ref={resultImgRef}
                        src={currentImg.src}
                        alt="Zoom preview"
                        onError={e => { e.currentTarget.src = PLACEHOLDER_MAIN; e.currentTarget.onerror = null; }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <TagBadges tags={otherTags} />

            {software && (
              <div className="hmi-software-section">
                <div className="hmi-software-header">
                  <img
                    src="/logo/dsync.png"
                    alt="Dsync Studio Logo"
                    className="hmi-software-logo"
                  />
                  <div>
                    <h3 className="hmi-software-title">{software.name}</h3>
                    <p className="hmi-software-platform">{software.platform}</p>
                  </div>
                </div>
                <p className="hmi-software-desc">
                  Dsync Studio by <span className="highlight-brand">BiBA Electronics Pvt Ltd</span> is a
                  powerful and intuitive Human-Machine Interface (HMI) design platform built for modern
                  embedded systems.
                </p>
                <button className="hmi-read-more-btn" onClick={() => navigate("/dsync-studio")}>
                  Read More →
                </button>
              </div>
            )}
          </div>

          {/* ── RIGHT col ── */}
          <div className="hmi-info-col">
            <div className="hmi-info-top">
              <div className="badges">
                <div className="hmi-badge">{product.badge}</div>
                <div className="hmi-badge">{product.badge1}</div>
              </div>
            </div>

            <div className="hmi-title-block">
              <h1 className="hmi-title" style={{ marginTop: "12px" }}>
                {product.title.split("\n").map((line, i, arr) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </h1>
            </div>
<div className="section-divider"></div>
            <div className="hmi-info-bottom">
              {product.subtitle.includes("{highlight}") ? (
                <Subtitle template={product.subtitle} highlight={product.highlight} />
              ) : (
               <div className="hmi-subtitle">
  {product.subtitle.split("\n").map((line, index) => (
    <p key={index}>{line}</p>
  ))}
</div>
              )}
<div className="section-divider"></div>
              <div className="price-cta-col">
                <div className="price-stock-row">

                  {showPrice && (
                    <div className="product-meta-item">
                      <span className="hmi-price-label">Price:</span>
                      <span className="hmi-price-value">
                        {overSlabLimit
                          ? "Contact Sales"
                          : hasPrice
                          ? `₹ ${Number(effectivePrice).toLocaleString()}`
                          : "-"}
                      </span>
                    </div>
                  )}

                  <div className="product-meta-item">
                    <span className="stock-label">Stock:</span>
                    <span className={`stock-value ${stockCount <= 0 ? "stock-value--out" : ""}`}>
                      {stockCount}
                    </span>
                  </div>
  {product.moq && (
    <div className="product-meta-item">

      <span className="stock-label">MOQ:</span>
      <span className="stock-value">
        {product.moq}
      </span>
    </div>
  )}

  {/* NEW LEAD TIME */}
  {product.leadTime && (
    <div className="product-meta-item">

      <span className="stock-label">Lead Time:</span>
      <span className="stock-value">
        {product.leadTime}
      </span>
    </div>
  )}
                </div>
<div className="section-divider"></div>
                <div className="cta-stack">
                  <div className="hmi-cta-row" ref={ctaRowRef}>

                    {showAddToCart && (
                      <button
                        className={`tft-add-cart-btn ${justAdded ? "tft-add-cart-btn--added" : ""}`}
                        onClick={handleAddToCart}
                        disabled={!hasPrice || overSlabLimit}
                        title={
                          overSlabLimit
                            ? `Contact sales for quantities above ${overSlabLimitQty}`
                            : hasPrice
                            ? undefined
                            : "Price coming soon"
                        }
                      >
                        <span className="tft-add-cart-btn-label">
                          <CartIcon className="tft-add-cart-btn-icon" size={17} />
                          {overSlabLimit ? "Contact Sales" : hasPrice ? "Add To Cart" : "Price coming soon"}
                        </span>
                        <span className="tft-add-cart-btn-check">✓ Added</span>
                      </button>
                    )}

                    {showQuantitySelector && (
                      <div className="qty-selector">

                        <button
                          type="button"
                          className="qty-btn"
                          onClick={decreaseQty}
                        >
                          -
                        </button>

                        <input
                          type="number"
                          min={effectiveMinQty}
                          value={quantity}
                          onChange={handleQtyChange}
                          onBlur={() => {
  const minQty = effectiveMinQty;

  if (quantity === "" || Number(quantity) < minQty) {
    setQuantity(minQty);
  }
}}
                          className="qty-input"
                        />

                        <button
                          type="button"
                          className="qty-btn"
                          onClick={increaseQty}
                        >
                          +
                        </button>

                      </div>
                    )}
                  </div>

                 

                  {product.datasheetUrl && (
                    <a
                      href={product.datasheetUrl}
                      download
                      className="tft-datasheet-btn datasheet-below"
                    >
                      <span className="tft-datasheet-icon">
                        <DownloadIcon size={16} />
                      </span>
                      Datasheet
                    </a>
                  )}

                  <button
                    type="button"
                    className={`tft-compare-btn ${inCompare ? "tft-compare-btn--active" : ""}`}
                    onClick={() => toggleCompare(compareProduct)}
                  >
                    {inCompare ? "✓ Added to Compare" : "+ Add to Compare"}
                  </button>

                </div>
                 {cartLimitMsg && (
                    <div className="cart-limit-msg-wrapper">
                      <span className="cart-limit-msg">
                        {cartLimitMsg}
                      </span>
                    </div>
                  )}

                {overSlabLimit && (
                  <div className="cart-limit-msg-wrapper">
                    <span className="cart-limit-msg">
                      For quantities above {overSlabLimitQty}, please contact sales for pricing.
                    </span>
                  </div>
                )}

                {hasPriceTiers && (
                  <BulkPricingInline priceTiers={priceTiers} activeTier={slabResult.tier} />
                )}

                <div className="hmi-trust-row">
                  <span className="hmi-trust-chip">
                    <span className="hmi-trust-chip-icon">🛡️</span> Genuine Product
                  </span>
                  <span className="hmi-trust-chip">
                    <span className="hmi-trust-chip-icon">🚚</span> Fast Dispatch
                  </span>
                  <span className="hmi-trust-chip">
                    <span className="hmi-trust-chip-icon">🔒</span> Secure Payment
                  </span>
                </div>
              </div>
            </div>

          </div>

          {(hasFbt || hasRelated) && (
            <div className="hmi-fbt-col">
              {hasFbt && <FrequentlyBoughtTogether frequentlyBoughtData={frequentlyBoughtTogether} />}
              {hasRelated && <RelatedProducts products={relatedProducts} />}
            </div>
          )}
        </div>

        
        {productDetails?.trim() && (
  <section className="product-details-card">

    <div className="product-details-section">

      <div className="product-details-heading">
        Product Details
      </div>

      <div className="product-details-content">
        {productDetails
          .split("\n")
          .filter(line => line.trim() !== "")
          .map((line, index) => (
            <p key={index}>{line}</p>
          ))}
      </div>

    </div>

  </section>
)}
        <div className="hmi-tabs-section">
          <div className="hmi-tab-panel">


             
              {bannersAboveSpecs && bannersAboveSpecs.length > 0 && (
                <div className="hmi-banners-section">
                  {bannersAboveSpecs.map((img, i) => (
                    <img
                      key={i}
                      className="hmi-product-banner"
                      src={img.src}
                      alt={img.alt}
                      onError={e => { e.currentTarget.style.display = "none"; }}
                    />
                  ))}
                </div>
              )}

              {generalSpecsNotes && (
                <div className="hmi-general-specs-notes">
                  {generalSpecsNotes.split("\n").map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              )}

              {specTabs.length > 0 && (
                <>
                  <div className="hmi-tabs-header">
                    {specTabs.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        className={`hmi-tab-btn${activeSpecTabKey === t.key ? " active" : ""}`}
                        onClick={() => setActiveSpecTab(t.key)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="spec-tab-active-panel">{activeSpecTabContent}</div>
                </>
              )}

              {bannersBelowSpecs && bannersBelowSpecs.length > 0 && (
                <div className="hmi-banners-section">
                  {bannersBelowSpecs.map((img, i) => (
                    <img
                      key={i}
                      className="hmi-product-banner"
                      src={img.src}
                      alt={img.alt}
                      onError={e => { e.currentTarget.style.display = "none"; }}
                    />
                  ))}
                </div>
              )}

              {/* ── Applications: still shown in the same flow if the
                   product data provides any. ── */}
              {applications && applications.length > 0 && (
                <div className="feature-grid">
                  {applications.map((a, i) => (
                    <div className="feature-card reveal-on-scroll" key={i} style={{ transitionDelay: `${i * 60}ms` }}>
                      <div className="feature-card-emoji">{a.emoji}</div>
                      <div className="feature-card-title">{a.title}</div>
                      <div className="feature-card-desc">{a.desc}</div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>

      
        {similarProducts?.length > 0 && (
    <SimilarProducts
        products={similarProducts}
    />
)} </div>

      {showPdf && (
        <PdfModal
          url={pdfUrl}
          onClose={() => { setShowPdf(false); setActiveImg(0); }}
        />
      )}

      

      {showAddToCart && (
        <div className={`hmi-sticky-bar ${showStickyBar ? "hmi-sticky-bar--visible" : ""}`}>
          <div className="hmi-sticky-bar-inner">
            <img
              className="hmi-sticky-bar-thumb"
              src={images?.[0]?.src || IMAGE_COMING_SOON}
              alt={product.title}
              onError={e => { e.currentTarget.style.visibility = "hidden"; }}
            />
            <div className="hmi-sticky-bar-info">
              <span className="hmi-sticky-bar-title">{product.title}</span>
              {showPrice && (
                <span className="hmi-sticky-bar-price">
                  {overSlabLimit ? "Contact Sales" : hasPrice ? `₹ ${Number(effectivePrice).toLocaleString()}` : "-"}
                </span>
              )}
            </div>

            {/* Mobile-only (hidden on desktop via CSS) — replaces the
                thumb/title/price with just a qty stepper, so the bar can
                be a plain full-width qty + Add to Cart strip on phones. */}
            {showQuantitySelector && (
              <div className="qty-selector hmi-sticky-bar-qty">
                <button type="button" className="qty-btn" onClick={decreaseQty}>
                  -
                </button>
                <input
                  type="number"
                  min={effectiveMinQty}
                  value={quantity}
                  onChange={handleQtyChange}
                  onBlur={() => {
                    const minQty = effectiveMinQty;
                    if (quantity === "" || Number(quantity) < minQty) {
                      setQuantity(minQty);
                    }
                  }}
                  className="qty-input"
                />
                <button type="button" className="qty-btn" onClick={increaseQty}>
                  +
                </button>
              </div>
            )}

            <button
              className={`tft-add-cart-btn hmi-sticky-bar-btn ${justAdded ? "tft-add-cart-btn--added" : ""}`}
              onClick={handleAddToCart}
              disabled={!hasPrice || overSlabLimit}
              title={
                overSlabLimit
                  ? `Contact sales for quantities above ${overSlabLimitQty}`
                  : hasPrice
                  ? undefined
                  : "Price coming soon"
              }
            >
              <span className="tft-add-cart-btn-label">
                <CartIcon className="tft-add-cart-btn-icon" size={17} />
                {overSlabLimit ? "Contact Sales" : hasPrice ? "Add To Cart" : "Price coming soon"}
              </span>
              <span className="tft-add-cart-btn-check">✓ Added</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SevenInchesPage;